import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import ShareIdeaRow from '../components/ShareIdeaRow';
import SaveAnswerPrompt from '../components/SaveAnswerPrompt';
import ExpertNoteByline from '../components/ExpertNoteByline';
import ExpertNoteFollowUpCta from '../components/ExpertNoteFollowUpCta';
import { API_BASE_URL } from '../config';
import { getCustomerUserId, isLoggedInCustomer, saveQuestionToAccount } from '../utils/customerAccount';
import {
  askLandscapingQuestion,
  listPublicLandscapingQuestions,
  postDesignerCommunityNote,
  getDesignerProfileForSession,
  designerProfileNoteLabel,
} from '../services/landscapingQuestionsApi';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

export default function DearJohnPage() {
  const currentUserId = usePublicUserId();
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const [question, setQuestion] = useState('');
  const [askerEmail, setAskerEmail] = useState('');
  const [notifyExpertNote, setNotifyExpertNote] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastAskId, setLastAskId] = useState(null);
  const [askError, setAskError] = useState(null);

  const [canPostNote, setCanPostNote] = useState(false);
  const [defaultDesignerLabel, setDefaultDesignerLabel] = useState('');
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState({});
  const noteLabelPrefilledRef = useRef(new Set());

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const list = await listPublicLandscapingQuestions(40);
      setItems(list);
    } catch (e) {
      setListError(e.message || 'Could not load questions.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    if (!token) {
      setCanPostNote(false);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/designers/community-questions?limit=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!cancelled) setCanPostNote(r.ok);
      })
      .catch(() => {
        if (!cancelled) setCanPostNote(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canPostNote) return;
    let cancelled = false;
    getDesignerProfileForSession()
      .then((p) => {
        if (!cancelled) setDefaultDesignerLabel(designerProfileNoteLabel(p));
      })
      .catch(() => {
        if (!cancelled) setDefaultDesignerLabel('');
      });
    return () => {
      cancelled = true;
    };
  }, [canPostNote]);

  useEffect(() => {
    if (!canPostNote || !defaultDesignerLabel || items.length === 0) return;
    setNoteDrafts((d) => {
      let changed = false;
      const next = { ...d };
      for (const item of items) {
        if (noteLabelPrefilledRef.current.has(item.id)) continue;
        const cur = next[item.id];
        if (cur && String(cur.label || '').trim()) {
          noteLabelPrefilledRef.current.add(item.id);
          continue;
        }
        if (!cur || !String(cur.label || '').trim()) {
          next[item.id] = { ...(cur || {}), text: cur?.text || '', label: defaultDesignerLabel };
          noteLabelPrefilledRef.current.add(item.id);
          changed = true;
        }
      }
      return changed ? next : d;
    });
  }, [canPostNote, defaultDesignerLabel, items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setSubmitting(true);
    setAskError(null);
    try {
      const data = await askLandscapingQuestion(question.trim(), {
        asker_email: askerEmail,
        notify_expert_note: notifyExpertNote,
      });
      setLastAskId(data.id || null);
      setSubmitted(true);
      setQuestion('');
      const cid = getCustomerUserId();
      if (cid && data.id) {
        saveQuestionToAccount(cid, {
          id: data.id,
          question_text: data.question_text || question.trim(),
          ai_answer: data.ai_answer || '',
          created_at: new Date().toISOString(),
          expert_notes: data.expert_notes || [],
        });
      }
      await loadList();
    } catch (err) {
      setAskError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostNote = async (questionId) => {
    const draft = noteDrafts[questionId] || {};
    const text = (draft.text || '').trim();
    if (!text) return;
    setNoteSaving((s) => ({ ...s, [questionId]: true }));
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const label =
        (draft.label || '').trim() ||
        defaultDesignerLabel ||
        userData.display_name ||
        userData.fullName ||
        userData.full_name ||
        'PlantADay Designer';
      await postDesignerCommunityNote(questionId, {
        advice_text: text,
        contributor_label: label,
      });
      setNoteDrafts((d) => ({ ...d, [questionId]: { text: '', label: draft.label || '' } }));
      await loadList();
    } catch (err) {
      alert(err.message || 'Could not submit note');
    } finally {
      setNoteSaving((s) => ({ ...s, [questionId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 w-full">

        <div className="mb-10">
          <Link to="/learn" className="text-sm font-medium text-green-700 hover:underline">
            ← Back to Learn
          </Link>
          <div className="mt-6">
            <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-2">
              A PlantADay column
            </p>
            <h1
              className="text-4xl sm:text-5xl text-gray-900 leading-tight"
              style={{ fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 700 }}
            >
              Dear John
            </h1>
            <p className="mt-3 text-lg text-gray-600 max-w-2xl">
              Real questions about yards and plants. AI answers first. John and his trusted experts add their take.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">

          <div className="flex-1 space-y-6 min-w-0">
            {loadingList && (
              <div className="flex items-center gap-2 text-gray-500 py-8">
                <Loader className="w-6 h-6 animate-spin" /> Loading questions…
              </div>
            )}
            {listError && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">{listError}</div>
            )}
            {!loadingList && !listError && items.length === 0 && (
              <p className="text-gray-600 text-sm">
                No published questions yet. Ask one from the form — AI answers first, then experts can add notes after review.
              </p>
            )}
            {!loadingList &&
              items.map((item) => (
                <article
                  key={item.id}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                  style={{ borderLeftWidth: '3px', borderLeftColor: '#16a34a' }}
                >
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Dear John,</p>
                  <p className="text-base text-gray-900 italic leading-relaxed whitespace-pre-wrap">{item.question_text}</p>
                  <hr className="my-4 border-gray-100" />
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{item.ai_answer}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      AI answered
                    </span>
                    {item.expert_notes && item.expert_notes.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Expert note added
                      </span>
                    )}
                  </div>
                  {item.expert_notes && item.expert_notes.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {item.expert_notes.map((note) => (
                        <div
                          key={note.id}
                          className="rounded-lg border border-blue-200 bg-blue-50/80 p-4 text-sm text-gray-800 whitespace-pre-wrap"
                        >
                          {note.advice_text}
                          {note.contributor_label && (
                            <div className="text-xs text-blue-900 font-medium mt-2">
                              <ExpertNoteByline note={note} className="text-xs text-blue-900 font-medium" />
                            </div>
                          )}
                          <ExpertNoteFollowUpCta note={note} className="text-xs text-gray-600 mt-2 leading-snug" />
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-400">
                    <Link to={`/ask-ai/${item.id}`} className="text-green-700 hover:underline font-medium">
                      Open this answer
                    </Link>
                  </p>

                  {canPostNote && (
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 mb-2">Add your expert note</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Submitted notes are reviewed before they appear here.
                      </p>
                      <input
                        type="text"
                        placeholder="Your name / label (e.g. Jane Smith, Landscape Designer)"
                        value={(noteDrafts[item.id] || {}).label || ''}
                        onChange={(e) =>
                          setNoteDrafts((d) => ({
                            ...d,
                            [item.id]: { ...d[item.id], label: e.target.value },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                      />
                      <textarea
                        rows={3}
                        placeholder="Practical advice or design direction…"
                        value={(noteDrafts[item.id] || {}).text || ''}
                        onChange={(e) =>
                          setNoteDrafts((d) => ({
                            ...d,
                            [item.id]: { ...d[item.id], text: e.target.value },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                      />
                      <button
                        type="button"
                        disabled={noteSaving[item.id] || !(noteDrafts[item.id] || {}).text?.trim()}
                        onClick={() => handlePostNote(item.id)}
                        className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {noteSaving[item.id] ? 'Submitting…' : 'Submit for review'}
                      </button>
                    </div>
                  )}
                </article>
              ))}
          </div>

          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Ask John and the team</h2>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                AI answers first. John and his trusted experts add their notes when they can.
              </p>

              {submitted ? (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800 leading-relaxed space-y-3">
                  <p className="font-semibold">Your AI answer is ready.</p>
                  {notifyExpertNote && askerEmail.trim() && (
                    <p>
                      We will email you at <span className="font-medium">{askerEmail.trim()}</span> when an expert publishes a note.
                    </p>
                  )}
                  {lastAskId && <ShareIdeaRow questionId={lastAskId} />}
                  {!isLoggedInCustomer() && <SaveAnswerPrompt />}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {askError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{askError}</div>
                  )}
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="Dear John, my rose bush has black spots on every leaf and I have tried everything..."
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  />
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyExpertNote}
                      onChange={(e) => setNotifyExpertNote(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span>Email me when an expert adds a note (optional)</span>
                  </label>
                  {notifyExpertNote && (
                    <div>
                      <input
                        type="email"
                        autoComplete="email"
                        value={askerEmail}
                        onChange={(e) => setAskerEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        We only use this to notify you about this question.
                      </p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !question.trim()}
                    className="w-full py-2.5 px-4 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Sending…' : 'Send question'}
                  </button>
                </form>
              )}

              <p className="mt-4 text-xs text-gray-400 leading-snug">
                No public thread. Answers may appear on this column after review.
              </p>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
